import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  adaptVariantPackMetadata,
  useAdaptVariantPackToItemListOption,
} from '@/contexts/item-context-utils';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { initialState as defaultQuantityState } from '@/screens/new-order/hooks/use-quantity-reducer';
import { EqualHeightTouchableOpacity } from '@/components/equal-height-touchable-opacity';
import { FlatList, Text, View } from 'react-native';
import { FONTS, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { gql } from '@apollo/client';
import { HorizontalListArrows } from '@/components/horizontal-list-arrows';
import { InboundDetailListItemAction } from './inbound-detail-list-item-action';
import { InboundDetailListItemHeader } from './inbound-detail-list-item-header';
import { InboundListItem } from './types';
import { InboundRenderActions } from './inbound-render-actions';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { OrderableItemListItem } from '@/components/orders/orderable-item-list-item';
import {
  OrderInbound_SearchUnmatchedItemsQuery,
  OrderInbound_SearchUnmatchedItemsQueryVariables,
} from '@/types';
import { SEARCH_VARIANTS_PACK_FRAGMENT } from '@/hooks/use-search-variant-packs';
import { SmartOrderListItemLoading } from '@/components/orders/smart-order-list-item-loading';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface Props {
  carouselItemWidth: number;
  inboundListItem: InboundListItem;
  inDraggableSheet?: boolean;
  listWidth: number | null;
  navigateToSearch: (item: InboundListItem) => void;
  onSwapItem: (nextItem: ItemListOptionMetadata) => void;
  restaurantUUID: string;
  showTranslation?: boolean;
  supplierUUID: string;
}
const GRAPHQL_ERROR_QUERY_LENGTH_EXCEEDED = 'QUERY_LENGTH_EXCEEDED';

export const SEARCH_UNMATCHED_VARIANT_PACKS = gql`
  query OrderInbound_SearchUnmatchedItems(
    $locale: String!
    $query: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    searchVariantPacks(
      include_order_counts: true
      query: $query
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      variant_pack {
        ...SearchCatalog_VariantPackFields
      }
      variant_pack_order_counts {
        days
        order_count
      }
    }
  }
  ${SEARCH_VARIANTS_PACK_FRAGMENT}
`;

export const InboundUnmatchedCarousel: React.FC<Props> = ({
  carouselItemWidth,
  inboundListItem,
  inDraggableSheet,
  listWidth,
  navigateToSearch,
  onSwapItem,
  restaurantUUID,
  showTranslation,
  supplierUUID,
}) => {
  const { selectItemListOption, closeDetailSheet } = useItemDetailContext();
  const { isLargeLayout } = useLayoutContext();
  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();
  const itemQueryVariables = useItemQueryVariables();
  const [styles, theme] = useStyles();
  const { isViewOnly } = useOrderInbounds();

  const maxLoadingSkeletons = listWidth
    ? Math.ceil(listWidth / carouselItemWidth)
    : 0;

  const { data, loading } = useWrappedQuery<
    OrderInbound_SearchUnmatchedItemsQuery,
    OrderInbound_SearchUnmatchedItemsQueryVariables
  >(SEARCH_UNMATCHED_VARIANT_PACKS, {
    context: {
      ignoreGraphQLErrorCodes: [GRAPHQL_ERROR_QUERY_LENGTH_EXCEEDED],
    },
    variables: {
      ...itemQueryVariables,
      query: inboundListItem.metadata!.inputWithResult.searchTerm,
      restaurantUUID,
      supplierUUID,
    },
  });

  const listItems = useMemo(() => {
    const searchVariantPacks = (data?.searchVariantPacks || []).reduce<
      ItemListOption[]
    >((acc, vpResponse) => {
      const { variant_pack, variant_pack_order_counts } = vpResponse;

      if (variant_pack) {
        if (variant_pack) {
          const adaptedVp = adaptVariantPackMetadata(
            adaptVariantPackToItemListOption(
              variant_pack,
              undefined,
              variant_pack_order_counts ?? [],
            ),
          );
          acc.push(adaptedVp);
        }
      }
      return acc;
    }, []);

    return searchVariantPacks;
  }, [data?.searchVariantPacks, adaptVariantPackToItemListOption]);

  if (!loading && listItems.length === 0) {
    return (
      <View
        style={
          isLargeLayout
            ? styles.noItemsContainerDesktop
            : styles.noItemsContainer
        }
      >
        <Text style={[TYPOGRAPHY.large, FONTS.primaryBold]}>
          {inboundListItem.title}
        </Text>

        <View
          style={
            isLargeLayout ? styles.noItemsContentDesktop : styles.noItemsContent
          }
        >
          <Typography variant="paragraph-md">
            {i18n.t('no_order_history.select_from_catalog', {
              item: inboundListItem.title,
            })}
          </Typography>

          <Button
            onPress={() => {
              navigateToSearch(inboundListItem);
            }}
            style={styles.searchCatalogButton}
            trackingData={{
              name: 'order_inbound_detail.search_catalog',
              properties: {
                searchTerm: inboundListItem.title,
              },
            }}
          >
            <Typography color={theme.sk.color.text.ondark} variant="label-md">
              {i18n.t('search_catalog')}
            </Typography>
          </Button>
        </View>
      </View>
    );
  }

  return (
    <>
      <InboundDetailListItemHeader
        inboundListItem={inboundListItem}
        showTranslation={showTranslation}
      >
        <InboundDetailListItemAction
          inboundListItem={inboundListItem}
          navigateToSearch={navigateToSearch}
        />
      </InboundDetailListItemHeader>
      <Text
        style={[TYPOGRAPHY.small, styles.noMatchText, styles.noMatchTextHeader]}
      >
        {i18n.t('no_order_history.select_from_catalog', {
          item: inboundListItem.title,
        })}
      </Text>
      {loading ? (
        <View style={styles.carouselLoading}>
          {Array(maxLoadingSkeletons)
            .fill(null)
            .map((_, i) => (
              <SmartOrderListItemLoading key={i} />
            ))}
        </View>
      ) : (
        <View style={styles.carouselContainer}>
          <HorizontalListArrows<ItemListOption>
            arrowStyle={styles.carouselArrows}
            trackingKey="order-inbound-unmatched"
          >
            {(listProps) => (
              <FlatList<ItemListOption>
                {...listProps}
                data={listItems}
                decelerationRate="fast"
                horizontal
                renderItem={({ item: carouselItem }) => {
                  const handleSwapItem = (variantPackUUID: string) => {
                    const skuToAdd = carouselItem.metadata!.skus.find((sku) => {
                      return sku.variantPackUUID === variantPackUUID;
                    });

                    if (skuToAdd) {
                      onSwapItem(skuToAdd);
                    }
                  };

                  const openItemDetailSheet = () => {
                    selectItemListOption(
                      carouselItem,
                      {
                        source: 'order_inbound_detail',
                      },
                      {
                        renderActions: (sku) => (
                          <InboundRenderActions
                            onSelect={() => {
                              handleSwapItem(sku.variantPackUUID);
                              closeDetailSheet();
                            }}
                          />
                        ),
                      },
                    );
                  };

                  return (
                    <EqualHeightTouchableOpacity
                      onPress={openItemDetailSheet}
                      style={{ width: carouselItemWidth }}
                    >
                      <OrderableItemListItem
                        containerStyle={styles.listItemContainer}
                        contentStyle={styles.listItemContent}
                        disabled={isViewOnly}
                        hideSampleRequest
                        inDraggableSheet={inDraggableSheet}
                        isFocused={false}
                        isInboundOrder
                        onOptionsPress={openItemDetailSheet}
                        option={carouselItem}
                        quantityState={defaultQuantityState}
                        showVariantPackOrderInfo
                        source="order_inbound_detail"
                        updateQuantity={({ variantPackUUID }) => {
                          handleSwapItem(variantPackUUID);
                        }}
                      />
                    </EqualHeightTouchableOpacity>
                  );
                }}
              />
            )}
          </HorizontalListArrows>
        </View>
      )}
    </>
  );
};

export const CAROUSEL_LOADING_MARGIN = 12;

const useStyles = createStyles((theme) => ({
  carouselArrows: {
    marginVertical: CAROUSEL_LOADING_MARGIN,
  },
  carouselContainer: {
    flex: 1,
  },
  carouselLoading: {
    flexDirection: 'row',
    gap: 16,
    marginLeft: CAROUSEL_LOADING_MARGIN,
    marginVertical: SCREEN_PADDING,
  },
  listItemContainer: {
    marginHorizontal: 0,
  },
  listItemContent: {
    height: '100%',
  },
  noItemsContainer: {
    gap: 16,
    marginHorizontal: 16,
  },
  noItemsContainerDesktop: {
    gap: 20,
    marginHorizontal: 16,
  },
  noItemsContent: {
    gap: 8,
    marginBottom: 16,
    width: '100%',
  },
  noItemsContentDesktop: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 24,
    justifyContent: 'space-between',
    marginBottom: 16,
    width: '100%',
  },
  noMatchText: {
    color: theme.colors.inactiveText,
    marginTop: 8,
  },
  noMatchTextHeader: {
    marginLeft: CAROUSEL_LOADING_MARGIN,
  },
  searchCatalogButton: {
    borderRadius: theme.sk.radii.radiusMedium,
    height: 40,
  },
}));
